if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this._onPrevClick = this._onPrevClick.bind(this);
        this._onNextClick = this._onNextClick.bind(this);
        this._onKeyDown = this._onKeyDown.bind(this);
      }

      connectedCallback() {
        if (super.connectedCallback) super.connectedCallback();

        this.navPrev = this.querySelector('.product-media-modal__nav-button--prev');
        this.navNext = this.querySelector('.product-media-modal__nav-button--next');

        // Prevent double-binding if the element is reconnected.
        if (this._navBound) return;
        this._navBound = true;

        // Use pointerup so we can stop propagation (ModalDialog closes media-modals on pointerup by default)
        this.navPrev?.addEventListener('pointerup', this._onPrevClick);
        this.navNext?.addEventListener('pointerup', this._onNextClick);

        // iOS Safari: avoid double-tap zoom on rapid taps.
        const preventDbl = (e) => {
          e.preventDefault();
          e.stopPropagation();
        };
        this.navPrev?.addEventListener('dblclick', preventDbl);
        this.navNext?.addEventListener('dblclick', preventDbl);
        this.querySelector('.product-media-modal__toggle')?.addEventListener('dblclick', preventDbl);
      }

      hide() {
        document.removeEventListener('keydown', this._onKeyDown);
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.setActiveMedia();
        document.addEventListener('keydown', this._onKeyDown);
      }

      _getMediaItems() {
        return Array.from(this.querySelectorAll('[data-media-id]'));
      }

      _getActiveIndex(items) {
        return items.findIndex((el) => el.classList.contains('active'));
      }

      _setActiveByIndex(delta) {
        const items = this._getMediaItems();
        if (!items.length) return;

        let idx = this._getActiveIndex(items);
        if (idx === -1) idx = 0;

        const nextIdx = (idx + delta + items.length) % items.length;
        const nextId = items[nextIdx].getAttribute('data-media-id');
        this.setActiveMedia(nextId);
      }

      _onPrevClick(event) {
        // Prevent the ModalDialog (media-modal) pointerup handler from closing the modal.
        event.preventDefault();
        event.stopPropagation();
        this._setActiveByIndex(-1);
      }

      _onNextClick(event) {
        // Prevent the ModalDialog (media-modal) pointerup handler from closing the modal.
        event.preventDefault();
        event.stopPropagation();
        this._setActiveByIndex(1);
      }

      _onKeyDown(event) {
        if (!this.hasAttribute('open')) return;

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this._setActiveByIndex(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this._setActiveByIndex(1);
        }
      }

      setActiveMedia(mediaId = null) {
        const targetId = mediaId || this.openedBy?.getAttribute('data-media-id');

        if (!targetId) return;

        let hasShownActive = false;
        this.querySelectorAll(`[data-media-id]`).forEach((element) => {
          const matchesTarget = element.getAttribute('data-media-id') === targetId;
          // If multiple nodes share the same data-media-id, only show the first one.
          const isActive = matchesTarget && !hasShownActive;
          if (isActive) hasShownActive = true;

          element.classList.toggle('active', isActive);

          // Hard-enforce visibility so we never render multiple images side-by-side.
          // (Some edge cases / cached CSS can leave non-active items visible.)
          element.style.setProperty('display', isActive ? 'block' : 'none', 'important');
        });

        const activeMedia =
          this.querySelector(`[data-media-id="${targetId}"]`) || this.querySelector('[data-media-id]');

        if (!activeMedia) return;

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

        activeMedia.scrollIntoView({ block: 'center', inline: 'center' });

        const container = this.querySelector('[role="document"]');
        if (container) container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }
    }
  );
}
